/**
 * Courtesy-only mirror of auth.check_resource/check_site in serving/auth.py. Hides a nav
 * item or a tab; never the actual rule -- the server enforces the real check on every
 * request regardless of what this returns. Kept in sync with serving/auth.py's
 * DEPARTMENT_GRANTS by hand, same as this file already mirrors is_admin.
 */
import { useSession, type Session } from './useSession'

const DEPARTMENT_GRANTS: Record<string, string[]> = {
  operations: ['reports'],
  safety: ['safety_reports'],
  maintenance: ['breakdown_reports'],
  battery_field_team: ['alerts'],
  call_centre: ['alerts', 'contact_info'],
  ground_ops: ['alerts'],
}

/** Admin-only at every site, whatever the department -- mirrors serving/auth.py's
 *  ADMIN_ONLY_RESOURCES ("the report is only shown to admin", 2026-11-03). */
export const ADMIN_ONLY_RESOURCES: readonly string[] = ['reports']

export function canResource(session: Session, resourceKey: string): boolean {
  if (session.is_admin) return true
  if (ADMIN_ONLY_RESOURCES.includes(resourceKey)) return false
  const grants = DEPARTMENT_GRANTS[session.department ?? ''] ?? []
  return grants.includes(resourceKey)
}

export function canSite(session: Session, site: string): boolean {
  if (session.is_admin) return true
  return (session.sites ?? []).includes(site)
}

export function useCan(resourceKey: string): boolean {
  const state = useSession()
  if (state.status !== 'signed-in') return false
  return canResource(state.session, resourceKey)
}

export function useSiteAccess(site: string): boolean {
  const state = useSession()
  if (state.status !== 'signed-in') return false
  return canSite(state.session, site)
}
